import React, { createContext, useContext, useState } from 'react';

import { useConversation } from './hooks/use_conversation';
import { useDropzone } from 'react-dropzone';

import type { Agent, Message, PageContext, Paths } from './types';

// Surfaces compose their own chrome around the conversation, so turn state lives here rather than in
// a shared shell that would need a prop for every difference between them.
//
// The seam is chrome, not transport - useAssistant is hardwired. Reuse from outside this module (the
// doc template editor panel being the likely one) differs on transport, on content rendering
// (markdown vs sanitized HTML) and on where tool calls land, so those three want injecting and this
// value wants an explicit interface rather than ReturnType.
//
// If mobile is dropped and the sidebar is the only surface left, collapse this back to props and
// keep a small context just for the transcript subtree (showAdminDetails, speaking).
type ConversationContextValue = ReturnType<typeof useConversation> & {
  agents: Agent[];
  firstName?: string | null;
  isAdmin: boolean;
  isDragActive: boolean;
  showAdminDetails: boolean;
  visible: boolean;
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  toggleAdminDetails: () => void;
};

const ConversationContext = createContext<ConversationContextValue | null>(null);

export function useConversationContext() {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversationContext must be used within a ConversationProvider');
  }

  return context;
}

interface Props {
  agents: Agent[];
  children: React.ReactNode;
  firstName?: string | null;
  isAdmin: boolean;
  pageContext: (messages: Message[]) => PageContext | null;
  paths: Paths;
  visible?: boolean;
}

export function ConversationProvider({
  agents,
  children,
  firstName,
  isAdmin,
  pageContext,
  paths,
  visible = true,
}: Props) {
  const conversation = useConversation({ agents, pageContext, paths, visible });
  const [showAdminDetails, setShowAdminDetails] = useState(false);

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDropAccepted: conversation.addAttachments,
  });

  const value = {
    ...conversation,
    agents,
    firstName,
    getRootProps,
    isAdmin,
    isDragActive,
    showAdminDetails,
    visible,
    toggleAdminDetails: () => setShowAdminDetails(prev => !prev),
  };

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
}
